/**
 * How a game reads on screen: dates, team names, monograms.
 *
 * Presentation only. Which game won, which filters apply and what counts as
 * logged are the server's answers; this module only spells them.
 */

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * "Sat Apr 11, 2026" from a stored YYYY-MM-DD, which is already the
 * venue-local calendar day. Parsed by parts, never through `new Date(iso)`:
 * that reads a bare date as UTC midnight and shows the day before anywhere
 * west of Greenwich.
 */
export function gameDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dow = new Date(y, m - 1, d).getDay();
  return `${DOW[dow]} ${MON[m - 1]} ${d}, ${y}`;
}

/** "October 2026", the month header on Upcoming. */
export function monthLabel(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  return y && m ? `${MONTH[m - 1]} ${y}` : "Unknown";
}

// Nicknames that are two words. Anything else is the last word of the name.
const TWO_WORD = new Set([
  "Red Wings", "Maple Leafs", "Blue Jackets", "Golden Knights", "Red Sox",
  "White Sox", "Blue Jays", "Trail Blazers", "Red Bulls",
]);

/** "Lightning" for "Tampa Bay Lightning": what a phone-width card shows. */
export function nickname(team: string): string {
  const w = team.trim().split(/\s+/);
  const lastTwo = w.slice(-2).join(" ");
  return TWO_WORD.has(lastTwo) ? lastTwo : w[w.length - 1] || team;
}

/** "TBL": initials for the circle a team without a cached logo gets. */
export function monogram(team: string): string {
  const w = team.replace(/[^A-Za-z ]/g, "").split(/\s+/).filter(Boolean);
  if (w.length === 0) return "?";
  return (w.length > 1 ? w.slice(0, 3).map((x) => x[0]).join("") : w[0].slice(0, 3)).toUpperCase();
}

/** A stable hue per team, so a monogram keeps its color across renders. */
export function teamHue(team: string): number {
  let h = 0;
  for (const c of team) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

/** "5 games", "1 game". */
export function games(n: number): string {
  return `${n} ${n === 1 ? "game" : "games"}`;
}
